import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { EventClickArg, EventContentArg } from '@fullcalendar/core';
import arLocale from '@fullcalendar/core/locales/ar';
import frLocale from '@fullcalendar/core/locales/fr';
import { useAuth } from '../../context/AuthContext';
import { lessonService, LessonResponse, DayOfWeek } from '../../services/lessonService';
import { studentService, Student } from '../../services/studentService';
import { absenceService } from '../../services/absenceService';
import { useModal } from '../../context/ModalContext';
import './Timetable.css';

const DAY_OFFSETS: Record<DayOfWeek, number> = {
  MONDAY: 0,
  TUESDAY: 1,
  WEDNESDAY: 2,
  THURSDAY: 3,
  FRIDAY: 4,
  SATURDAY: 5,
  SUNDAY: 6,
};

const getCurrentWeekStart = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

interface CalendarEvent {
  id: string;
  title?: string;
  start: Date;
  end: Date;
  extendedProps: LessonResponse & { durationText?: string };
  backgroundColor?: string;
  borderColor?: string;
  classNames?: string[];
}

const TeacherTimetable: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { showAlert } = useModal();

  const [lessons, setLessons] = useState<LessonResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLesson, setSelectedLesson] = useState<LessonResponse | null>(null);
  const [selectedLessonDate, setSelectedLessonDate] = useState<Date | null>(null);

  // Absence/Attendance state
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [absentStudentIds, setAbsentStudentIds] = useState<string[]>([]);
  const [currentLessonForAttendance, setCurrentLessonForAttendance] = useState<LessonResponse | null>(null);
  const [currentAttendanceDate, setCurrentAttendanceDate] = useState<Date | null>(null);

  // Get locale for FullCalendar
  const calendarLocale = useMemo(() => {
    switch (i18n.language) {
      case 'ar': return arLocale;
      case 'fr': return frLocale;
      default: return undefined;
    }
  }, [i18n.language]);

  const calendarDirection = i18n.language === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    loadLessons();
  }, [user?.id]);

  const loadLessons = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await lessonService.getLessonsByTeacher(user.id);
      setLessons(data);
      setError('');
    } catch (err: any) {
      setError(t('timetable.failedToLoad'));
      console.error('Failed to load lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate consistent color for a course
  const getCourseColor = (courseId: string): string => {
    let hash = 0;
    for (let i = 0; i < courseId.length; i++) {
      hash = courseId.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 45 + (Math.abs(hash >> 8) % 15);
    const lightness = 65 + (Math.abs(hash >> 16) % 10);

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Find next upcoming lesson
  const getNextLesson = (): string | null => {
    if (lessons.length === 0) return null;

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const dayMap: { [key in DayOfWeek]: number } = {
      SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3,
      THURSDAY: 4, FRIDAY: 5, SATURDAY: 6
    };

    let nextLessonId: string | null = null;
    let minDiff = Infinity;

    lessons.forEach((lesson: LessonResponse) => {
      const lessonDay = dayMap[lesson.dayOfWeek];
      const [lessonHour, lessonMin] = lesson.startTime.split(':').map(Number);
      const lessonTime = lessonHour * 60 + lessonMin;

      let dayDiff = lessonDay - currentDay;
      if (dayDiff < 0) dayDiff += 7;

      let totalDiff = dayDiff * 24 * 60 + (lessonTime - currentTime);

      if (totalDiff > 0 && totalDiff < minDiff) {
        minDiff = totalDiff;
        nextLessonId = lesson.id;
      }
    });

    return nextLessonId;
  };

  const nextLessonId = useMemo(() => getNextLesson(), [lessons]);

  // Convert lessons to FullCalendar events
  const events: CalendarEvent[] = useMemo(() => {
    const weekStart = getCurrentWeekStart();
    return lessons.map(lesson => {
      const lessonDate = new Date(weekStart);
      lessonDate.setDate(weekStart.getDate() + DAY_OFFSETS[lesson.dayOfWeek]);

      const [startHour, startMin] = lesson.startTime.split(':').map(Number);
      const [endHour, endMin] = lesson.endTime.split(':').map(Number);

      const start = new Date(lessonDate);
      start.setHours(startHour, startMin, 0, 0);

      const end = new Date(lessonDate);
      end.setHours(endHour, endMin, 0, 0);

      const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      const durationText = hours > 0
        ? `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`
        : `${minutes}min`;

      const isNextLesson = lesson.id === nextLessonId;
      const courseColor = getCourseColor(lesson.courseId);

      return {
        id: lesson.id,
        title: lesson.courseName,
        start,
        end,
        backgroundColor: courseColor,
        borderColor: isNextLesson ? '#ff8c00' : 'transparent',
        classNames: isNextLesson ? ['next-lesson'] : [],
        extendedProps: { ...lesson, durationText }
      };
    });
  }, [lessons, nextLessonId]);

  const handleEventClick = (clickInfo: EventClickArg) => {
    const lesson = clickInfo.event.extendedProps as LessonResponse;
    setSelectedLesson(lesson);
    setSelectedLessonDate(clickInfo.event.start);
  };

  const formatDuration = (startTime: string, endTime: string): string => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const duration = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    return `${duration} ${t('timetable.minutes')}`;
  };

  // Custom event content renderer
  const renderEventContent = (eventInfo: EventContentArg) => {
    const lesson = eventInfo.event.extendedProps as LessonResponse & { durationText?: string };
    const isNextLesson = eventInfo.event.id === nextLessonId;

    return (
      <div className="fc-event-content-custom">
        {isNextLesson && (
          <div className="next-badge">NEXT</div>
        )}
        <div className="event-course-name">{lesson.courseName}</div>
        <div className="event-time">{lesson.startTime} - {lesson.endTime}</div>
        <div className="event-duration">{lesson.durationText}</div>
        {lesson.room && <div className="event-room">📍 {lesson.room}</div>}
      </div>
    );
  };

  // --- Absence System Handlers ---

  const openAttendanceModal = async (lesson: LessonResponse, date: Date | null) => {
    if (!date) {
      showAlert(t('common.error'), "Error: No date selected for this lesson");
      return;
    }
    setCurrentLessonForAttendance(lesson);
    setCurrentAttendanceDate(date);
    setIsAttendanceModalOpen(true);
    setSelectedLesson(null);
    setSelectedLessonDate(null);

    try {
      setLoading(true);
      const studentsResp = await studentService.searchStudents({
        page: 0,
        size: 1000,
        filters: { classId: lesson.classeId }
      });
      setClassStudents(studentsResp.content);

      const dateStr = date.toISOString().split('T')[0];
      const absences = await absenceService.getAbsencesByLesson(lesson.id, dateStr);
      setAbsentStudentIds(absences.map(a => a.studentId));
    } catch (e) {
      console.error(e);
      showAlert(t('common.error'), t('absences.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const closeAttendanceModal = () => {
    setIsAttendanceModalOpen(false);
    setCurrentLessonForAttendance(null);
    setCurrentAttendanceDate(null);
    setClassStudents([]);
    setAbsentStudentIds([]);
  };

  const toggleStudentAbsence = (studentId: string) => {
    setAbsentStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleAttendanceSave = async () => {
    if (!currentLessonForAttendance || !currentAttendanceDate) return;
    try {
      const dateStr = currentAttendanceDate.toISOString().split('T')[0];
      await absenceService.bulkUpdateAbsences({
        lessonId: currentLessonForAttendance.id,
        lessonDate: dateStr,
        studentIds: absentStudentIds
      });
      showAlert(t('common.success'), t('absences.saved'));
      closeAttendanceModal();
    } catch (e) {
      console.error(e);
      showAlert(t('common.error'), t('absences.failedToSave'));
    }
  };

  // -------------------------------

  if (loading && lessons.length === 0) {
    return <div className="timetable-container">{t('common.loading')}</div>;
  }

  return (
    <div className="timetable-container">
      <div className="timetable-header">
        <h1>{t('timetable.myTimetable')}</h1>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="calendar-container">
        <FullCalendar
          plugins={[timeGridPlugin]}
          initialView="timeGridWeek"
          headerToolbar={false}
          locale={calendarLocale}
          direction={calendarDirection}
          events={events}
          height={1000}
          slotMinTime="07:00:00"
          slotMaxTime="23:00:00"
          slotDuration="00:30:00"
          allDaySlot={false}
          selectable={false}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          nowIndicator={true}
          weekends={true}
          firstDay={1}
        />
      </div>

      {lessons.length === 0 && !loading && (
        <div className="empty-state">
          <p>{t('timetable.noLessonsScheduled')}</p>
        </div>
      )}

      {/* Lesson Detail Modal */}
      {selectedLesson && (
        <div className="modal-overlay" onClick={() => { setSelectedLesson(null); setSelectedLessonDate(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('timetable.lesson')}</h2>
              <button className="modal-close" onClick={() => { setSelectedLesson(null); setSelectedLessonDate(null); }}>×</button>
            </div>

            <div className="modal-body">
              <div className="lesson-detail">
                <div className="detail-row">
                  <strong>{t('timetable.course')}:</strong>
                  <span>{selectedLesson.courseName}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('timetable.class')}:</strong>
                  <span>{selectedLesson.className}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('timetable.dayOfWeek')}:</strong>
                  <span>{t(`timetable.days.${selectedLesson.dayOfWeek}`)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('timetable.time')}:</strong>
                  <span>{selectedLesson.startTime} - {selectedLesson.endTime}</span>
                </div>
                <div className="detail-row">
                  <strong>{t('timetable.duration')}:</strong>
                  <span>{formatDuration(selectedLesson.startTime, selectedLesson.endTime)}</span>
                </div>
                {selectedLesson.room && (
                  <div className="detail-row">
                    <strong>{t('timetable.room')}:</strong>
                    <span>{selectedLesson.room}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="modal-footer-left">
                <button
                  className="btn-info"
                  onClick={() => openAttendanceModal(selectedLesson, selectedLessonDate)}
                  style={{ backgroundColor: '#2196F3', color: 'white', marginRight: 'auto' }}
                >
                  📅 {t('absences.markAttendance')} {selectedLessonDate ? `(${selectedLessonDate.toLocaleDateString()})` : ''}
                </button>
              </div>
              <div className="modal-footer-right">
                <button className="btn-secondary" onClick={() => { setSelectedLesson(null); setSelectedLessonDate(null); }}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {isAttendanceModalOpen && (
        <div className="modal-overlay" onClick={closeAttendanceModal}>
          <div className="modal-content attendance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('absences.attendance')} - {currentLessonForAttendance?.courseName}</h2>
              <button className="modal-close" onClick={closeAttendanceModal}>×</button>
            </div>

            <div className="modal-body">
              <p className="attendance-subtitle">
                {currentAttendanceDate?.toLocaleDateString()} • {currentLessonForAttendance?.startTime} - {currentLessonForAttendance?.endTime}
              </p>

              <div className="students-list">
                {classStudents.length === 0 ? (
                  <p>{t('students.noStudentsFound')}</p>
                ) : (
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th>{t('absences.studentName')}</th>
                        <th>{t('absences.status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classStudents.map(student => {
                        const isAbsent = absentStudentIds.includes(student.id!);
                        return (
                          <tr key={student.id} className={isAbsent ? 'absent-row' : ''}>
                            <td>{student.firstName} {student.lastName}</td>
                            <td>
                              <button
                                className={`attendance-toggle ${isAbsent ? 'absent' : 'present'}`}
                                onClick={() => toggleStudentAbsence(student.id!)}
                              >
                                {isAbsent ? t('absences.absent') : t('absences.present')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeAttendanceModal}>
                {t('common.cancel')}
              </button>
              <button className="btn-primary" onClick={handleAttendanceSave}>
                {t('absences.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherTimetable;
